/**
 * One quote, three places: the on-screen quote (Review & send → View), the PDF, and the Zoho Quote.
 * All three are built from quoteModel(), so the numbers can never disagree.
 *
 * Summary (pre-selected): every service with its description, then subtotal, discount, HST, total.
 * Detailed: the same plus Qty / Rate / Amount on every line.
 * Valid for 90 days from the day it's sent — a resend restarts the 90 days.
 */
import { CATEGORY_ORDER, getCatalog } from './catalog';
import { packageTotal } from '../pages/Routes/reportData';

export const COMPANY = {
  name: 'Canadian Insulation Experts',
  street: '244 Bartley Dr',
  cityline: 'North York, ON M4A 1G1',
  phone: '(416) 990-4555',
  email: 'support@canadianinsulationexperts.com',
};
export const QUOTE_VALID_DAYS = 90;
// still to come from Heemo (on the to-do list): the quote terms text
export const QUOTE_TERMS = '';
// BuildRight Renovations Inc. (operates CIE) — BN 743047433, HST account RT0001
export const HST_NUMBER = '743047433 RT0001';

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const money = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const longDate = (d) => new Date(d).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' });

// calendar days (not 24-hour blocks), so the clock change in November can't shift the date
export const validUntil = (sentAt) => {
  const d = new Date(sentAt);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + QUOTE_VALID_DAYS);
};

const lineAmount = (s) => r2((s.quantity || 1) * (s.unitRate || 0));

/* Subtotal → discount → HST 13% on the discounted amount → total (same maths as the pricing page). */
export const quoteTotals = (pkg) => {
  const subtotal = r2((pkg.services || []).reduce((sum, s) => sum + lineAmount(s), 0));
  const d = pkg.discount;
  let discount = 0;
  if (d && d.type === 'amount') discount = Number(d.value) || 0;
  if (d && d.type === 'percent') discount = (subtotal * (Number(d.value) || 0)) / 100;
  discount = r2(Math.min(discount, subtotal));
  const total = packageTotal(pkg);
  return { subtotal, discount, discountName: (d && d.name) || 'Discount', hst: r2(total - (subtotal - discount)), total };
};

// the customer text for a line: what was saved on it, else the product's description in Zoho
// (also the report's quote breakdown, so the report and the PDF describe each item the same way)
export const descriptionFor = (s, items = getCatalog().items) => {
  if (s.description) return s.description;
  const item = items.find((i) => (s.productId && i.id === s.productId) || i.name === s.name);
  return (item && item.description) || '';
};

const qtyLabel = (s) => {
  const q = Number(s.quantity) || 1;
  const n = q.toLocaleString('en-CA', { maximumFractionDigits: 2 });
  return s.unit === 'SQFT' ? `${n} sq ft` : n;
};

/**
 * Everything the quote shows.
 * customer: { name, address, email, phone } · agent: who prepared it
 * quoteNumber: Zoho's (Q-123456) once saved, else blank · sentAt: when it went out (now for a preview)
 */
export const quoteModel = ({ pkg, format = 'summary', customer = {}, agent = '', quoteNumber = '', sentAt = new Date(), items = getCatalog().items }) => {
  const byCat = new Map();
  (pkg.services || []).forEach((s) => {
    const cat = s.category || 'Other services';
    if (!byCat.has(cat)) byCat.set(cat, []);
    const amount = lineAmount(s);
    byCat.get(cat).push({
      name: s.name,
      description: descriptionFor(s, items),
      qty: qtyLabel(s),
      rate: amount ? money(s.unitRate) : '—',
      amount: amount ? money(amount) : 'Included',
      included: !amount,
    });
  });
  const rank = (c) => (CATEGORY_ORDER.indexOf(c) === -1 ? CATEGORY_ORDER.length : CATEGORY_ORDER.indexOf(c));
  const groups = [...byCat.entries()].sort(([a], [b]) => rank(a) - rank(b)).map(([category, lines]) => ({ category, lines }));
  const t = quoteTotals(pkg);
  return {
    format: format === 'detailed' ? 'detailed' : 'summary',
    title: pkg.name || 'Quote',
    number: quoteNumber,
    date: longDate(sentAt),
    validUntil: longDate(validUntil(sentAt)),
    customer,
    agent,
    company: COMPANY,
    groups,
    totals: {
      subtotal: money(t.subtotal),
      discount: t.discount ? `-${money(t.discount)}` : '',
      discountName: t.discountName,
      hst: money(t.hst),
      total: money(t.total),
    },
    terms: QUOTE_TERMS,
    hstNumber: HST_NUMBER,
  };
};

/* What App 6 saves in Zoho for one package. */
export const zohoQuotePayload = (pkg, format, zohoId) => {
  const t = quoteTotals(pkg);
  const items = getCatalog().items;
  return {
    key: String(pkg.id),
    zohoId: zohoId || pkg.zohoId || undefined,
    name: pkg.name || 'Quote',
    format: format === 'detailed' ? 'detailed' : 'summary',
    discountAmount: t.discount,
    discountName: t.discount ? t.discountName : '',
    hst: t.hst,
    lines: (pkg.services || []).map((s) => ({
      productId: s.productId,
      name: s.name,
      quantity: Number(s.quantity) || 1,
      rate: Number(s.unitRate) || 0,
      description: descriptionFor(s, items),
    })),
  };
};
